import React, { useState, useEffect, useCallback } from 'react';
import { Send, Loader2, Sparkles, Sliders, ShieldCheck, FileText, BookOpen, Search, Wrench, Check, X, ClipboardList, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const MODES = [
  { id: 'search', label: 'Rechercher une solution', icon: <Search className="h-3.5 w-3.5" /> },
  { id: 'improve_article', label: 'Modifier une fiche', icon: <FileText className="h-3.5 w-3.5" /> },
  { id: 'create_procedure', label: 'Créer une procédure', icon: <BookOpen className="h-3.5 w-3.5" /> },
  { id: 'improve_procedure', label: 'Modifier une procédure', icon: <Wrench className="h-3.5 w-3.5" /> },
  { id: 'analyze', label: 'Analyser un dépannage', icon: <Wrench className="h-3.5 w-3.5" /> },
  { id: 'correct_answer', label: 'Corriger une réponse Nova', icon: <Sparkles className="h-3.5 w-3.5" /> },
  { id: 'settings', label: 'Modifier les réglages', icon: <Sliders className="h-3.5 w-3.5" /> },
  { id: 'compare', label: 'Comparer plusieurs solutions', icon: <ClipboardList className="h-3.5 w-3.5" /> },
];

const CONF = { high: 'text-[#00FF88]', medium: 'text-[#FFB800]', low: 'text-[#FF3B5C]' };

const ActionCard = ({ action, onApply, applying }) => {
  const [cmp, setCmp] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const isSetting = action.type === 'update_setting';
  const blocked = action.blocked || !action.can_apply;

  const compare = async () => {
    setCmpLoading(true);
    try {
      const r = await api.post('/admin/assistant/ai-chat/compare', { action });
      setCmp(r.data);
    } catch (e) { toast.error('Comparaison impossible.'); }
    finally { setCmpLoading(false); }
  };

  const canPublish = cmp ? (cmp.validation && cmp.validation.valid) : false;

  return (
    <div className="rounded-lg border border-[#3A3A3D] bg-[#0A0A0B] p-3 mt-2" data-testid="ai-proposed-action">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-3.5 w-3.5 text-[#FF6B35]" />
        <span className="text-xs font-semibold text-white">{action.type}</span>
        {action.requires_permission && (
          <span className="text-[10px] text-[#6B6B6E]">niveau : {action.requires_permission}</span>
        )}
      </div>
      {isSetting && (
        <div className="text-xs text-[#B8B8BA] space-y-0.5 mb-2">
          <p><span className="text-[#6B6B6E]">Réglage :</span> {action.setting}</p>
          <p><span className="text-[#6B6B6E]">Valeur actuelle :</span> {String(action.current_value)}</p>
          <p><span className="text-[#6B6B6E]">Valeur proposée :</span> <span className="text-[#FF6B35]">{String(action.proposed_value)}</span></p>
          {action.preview?.difference && <p><span className="text-[#6B6B6E]">Différence :</span> {action.preview.difference}</p>}
          {action.impact && <p className="text-[#6B6B6E] italic">{action.impact}</p>}
          {(action.preview?.risks || []).length > 0 && (
            <ul className="list-disc list-inside text-[#FFB800]">
              {action.preview.risks.map((rk, i) => <li key={i}>{rk}</li>)}
            </ul>
          )}
        </div>
      )}
      {!isSetting && (
        <p className="text-xs text-[#B8B8BA] mb-2">
          {action.article?.title || action.procedure?.title || action.procedure?.slug || 'Brouillon proposé (à comparer avant publication).'}
        </p>
      )}
      {action.block_reason && <p className="text-[11px] text-[#FF3B5C] mb-2">{action.block_reason}</p>}

      {/* Comparaison avant/après (fiche / procédure) */}
      {!isSetting && (
        <button onClick={compare} disabled={cmpLoading}
          className="text-[11px] px-2.5 py-1 rounded-lg border border-[#00FFFF]/40 text-[#00FFFF] mb-2 disabled:opacity-40"
          data-testid="ai-action-compare">
          {cmpLoading ? 'Comparaison…' : 'Comparer (avant/après)'}
        </button>
      )}
      {cmp && !isSetting && (
        <div className="rounded-lg border border-[#3A3A3D] bg-[#111113] p-2 mb-2 text-[11px] space-y-1" data-testid="ai-action-diff">
          {cmp.duplicate && (
            <p className="text-[#FFB800]">Fiche similaire existante : « {cmp.duplicate.title} ». Envisagez de la compléter ou de créer une nouvelle version.</p>
          )}
          {(cmp.added || []).length > 0 && (
            <p className="text-[#00FF88]">Ajouts : {cmp.added.map((a) => a.field).join(', ')}</p>
          )}
          {(cmp.modified || []).length > 0 && (
            <div className="text-[#B8B8BA]">Modifications :
              {cmp.modified.map((m, i) => (
                <p key={i} className="ml-2">• {m.field}</p>
              ))}
            </div>
          )}
          {cmp.steps_diff && (
            <p className="text-[#B8B8BA]">Étapes : {cmp.steps_diff.before_count} → <span className="text-[#FF6B35]">{cmp.steps_diff.after_count}</span></p>
          )}
          {cmp.impact && ((cmp.impact.devices || []).length > 0 || (cmp.impact.apps || []).length > 0 || (cmp.impact.routing || []).length > 0) && (
            <p className="text-[#6B6B6E]">Impact — appareils : {(cmp.impact.devices || []).join(', ') || '—'} · applications : {(cmp.impact.apps || []).join(', ') || '—'} · routage : {(cmp.impact.routing || []).join(', ') || '—'}</p>
          )}
          {cmp.validation && !cmp.validation.valid && (
            <ul className="list-disc list-inside text-[#FF3B5C]">
              {(cmp.validation.errors || []).map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          )}
          {cmp.validation && cmp.validation.valid && <p className="text-[#00FF88]">Validation : conforme, publication possible.</p>}
        </div>
      )}

      {isSetting ? (
        <button disabled={blocked || applying} onClick={() => onApply(action)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40 hover:opacity-90"
          data-testid="ai-apply-action">
          {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Confirmer et appliquer
        </button>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button disabled={blocked || applying} onClick={() => onApply({ ...action, mode: 'draft' })}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-white disabled:opacity-40"
            data-testid="ai-apply-draft">Enregistrer comme brouillon</button>
          <button disabled={blocked || applying} onClick={() => onApply({ ...action, mode: 'test' })}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#00FFFF]/15 border border-[#00FFFF]/40 text-[#00FFFF] disabled:opacity-40"
            data-testid="ai-apply-test">Tester</button>
          <button disabled={blocked || applying || !canPublish} onClick={() => onApply({ ...action, mode: 'publish' })}
            title={canPublish ? '' : 'Comparez d\'abord — publication possible uniquement si conforme.'}
            className="text-xs px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40"
            data-testid="ai-apply-publish">Publier</button>
        </div>
      )}
    </div>
  );
};

export default function AdminAIChat() {
  const [mode, setMode] = useState('search');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [settings, setSettings] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [webStatus, setWebStatus] = useState(null);
  // Cible d'édition (procédure/fiche) pour les modes « Modifier »
  const [procedures, setProcedures] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editTarget, setEditTarget] = useState('');

  // Réinitialise la cible + le fil quand on change de mode ; charge les listes utiles.
  useEffect(() => {
    setEditTarget('');
    setConversationId(null);
    setMessages([]);
    if (mode === 'improve_procedure') {
      api.get('/admin/assistant/procedures').then((r) => setProcedures(r.data.items || r.data || [])).catch(() => {});
    } else if (mode === 'improve_article') {
      api.get('/assistant/tutorials?limit=100').then((r) => setArticles(r.data.items || [])).catch(() => {});
    }
  }, [mode]);

  const loadSettings = useCallback(() => {
    api.get('/admin/assistant/ai-chat/settings').then((r) => {
      setSettings(r.data.settings || []);
      setWebStatus(r.data.web_research || null);
    }).catch(() => {});
  }, []);
  const loadAudit = useCallback(() => {
    api.get('/admin/assistant/ai-chat/audit').then((r) => setAudit(r.data.items || [])).catch(() => {});
  }, []);

  // --- Édition d'un réglage avec aperçu avant/après ---
  const [editing, setEditing] = useState(null); // { key, value }
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // --- Test de routage (effet immédiat du seuil) ---
  const [testMsg, setTestMsg] = useState('');
  const [testThreshold, setTestThreshold] = useState('');
  const [testResult, setTestResult] = useState(null);
  // --- Simulation prédictive d'impact ---
  const [simSampleCount, setSimSampleCount] = useState('500');
  const [simSamplePeriod, setSimSamplePeriod] = useState('');
  const [simDeep, setSimDeep] = useState(false);
  const [simAiLimit, setSimAiLimit] = useState('20');
  const [simResult, setSimResult] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  // Simulation approfondie (Claude) — estimation puis exécution à la demande
  const [deepLimit, setDeepLimit] = useState('10');
  const [deepEstimate, setDeepEstimate] = useState(null);
  const [deepResult, setDeepResult] = useState(null);
  const [deepBusy, setDeepBusy] = useState(false);
  // Sources Web officielles (Tavily)
  const [srcQuery, setSrcQuery] = useState('');
  const [srcResults, setSrcResults] = useState(null);
  const [srcBusy, setSrcBusy] = useState(false);
  // Score de qualité du routeur (verdicts + batches approfondis)
  const [quality, setQuality] = useState(null);
  const [qualityGroup, setQualityGroup] = useState('');
  const [qualityBusy, setQualityBusy] = useState(false);

  const loadQuality = useCallback((group) => {
    setQualityBusy(true);
    const g = group !== undefined ? group : qualityGroup;
    api.get(`/admin/assistant/routing-quality/score${g ? `?group_by=${g}` : ''}`)
      .then((r) => setQuality(r.data))
      .catch(() => {})
      .finally(() => setQualityBusy(false));
  }, [qualityGroup]);

  const estimateDeep = async () => {
    setDeepBusy(true); setDeepEstimate(null); setDeepResult(null);
    try {
      const sample = simSamplePeriod ? { mode: 'period', days: parseInt(simSamplePeriod) } : { mode: 'count', count: parseInt(simSampleCount) };
      const r = await api.post('/admin/assistant/ai-chat/simulate/deep-estimate', { sample, ai_call_limit: parseInt(deepLimit) });
      setDeepEstimate(r.data);
    } catch (e) { toast.error('Estimation impossible.'); }
    finally { setDeepBusy(false); }
  };
  const runDeep = async () => {
    setDeepBusy(true); setDeepResult(null);
    try {
      const sample = simSamplePeriod ? { mode: 'period', days: parseInt(simSamplePeriod) } : { mode: 'count', count: parseInt(simSampleCount) };
      const r = await api.post('/admin/assistant/ai-chat/simulate/deep-run', { sample, ai_call_limit: parseInt(deepLimit) });
      const jobId = r.data.job_id;
      toast.info('Simulation lancée — analyse en arrière-plan…');
      // Interrogation périodique (le batch dépasse le délai du proxy en synchrone)
      const poll = async (attempt) => {
        try {
          const s = await api.get(`/admin/assistant/ai-chat/simulate/deep-run/${jobId}`);
          if (s.data.status === 'done') {
            setDeepResult(s.data);
            toast.success(`Simulation approfondie : ${s.data.ai_calls_made} appels`);
            setDeepBusy(false);
            loadQuality();
            return;
          }
          if (s.data.status === 'error') {
            toast.error(s.data.error || 'Simulation approfondie impossible.');
            setDeepBusy(false);
            return;
          }
        } catch (e) { /* on continue à interroger */ }
        if (attempt >= 90) { toast.error('Délai dépassé pour la simulation.'); setDeepBusy(false); return; }
        setTimeout(() => poll(attempt + 1), 3000);
      };
      poll(0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Simulation approfondie impossible.'); setDeepBusy(false); }
  };
  const recordVerdict = async (comparison, verdict) => {
    try { await api.post('/admin/assistant/ai-chat/routing-validation', { comparison, verdict }); toast.success('Validation enregistrée'); loadQuality(); }
    catch (e) { toast.error('Erreur'); }
  };
  const searchSources = async () => {
    if (!srcQuery.trim()) return;
    setSrcBusy(true); setSrcResults(null);
    try {
      const r = await api.post('/admin/assistant/kb/search-sources', { query: srcQuery });
      setSrcResults(r.data);
    } catch (e) { toast.error('Recherche impossible.'); }
    finally { setSrcBusy(false); }
  };

  const runSimulation = async (change) => {
    setSimBusy(true);
    setSimResult(null);
    try {
      const sample = simSamplePeriod
        ? { mode: 'period', days: parseInt(simSamplePeriod) }
        : { mode: 'count', count: parseInt(simSampleCount) };
      const body = { change, sample, deep: simDeep };
      if (simDeep) body.ai_call_limit = parseInt(simAiLimit || '0');
      const r = await api.post('/admin/assistant/ai-chat/simulate', body);
      setSimResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Simulation impossible.');
    } finally {
      setSimBusy(false);
    }
  };

  const requestPreview = async (key, value) => {
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.post('/admin/assistant/ai-chat/settings/preview', { setting: key, proposed_value: value });
      setPreview(r.data);
    } catch (e) {
      toast.error("Impossible de générer l'aperçu.");
    } finally {
      setBusy(false);
    }
  };

  const applySetting = async (key, value) => {
    setBusy(true);
    try {
      const r = await api.post('/admin/assistant/ai-chat/settings/apply', { setting: key, proposed_value: value });
      toast.success(`Réglage appliqué : ${key} = ${r.data.after} (effet immédiat)`);
      setEditing(null);
      setPreview(null);
      loadSettings();
      loadAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Valeur refusée.');
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    if (!testMsg.trim()) return;
    setBusy(true);
    setTestResult(null);
    try {
      const body = { message: testMsg };
      if (testThreshold !== '') body.threshold = parseFloat(testThreshold);
      const r = await api.post('/admin/assistant/ai-chat/settings/test', body);
      setTestResult(r.data);
    } catch (e) {
      toast.error('Test impossible.');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { if (showSettings && !quality) loadQuality(''); }, [showSettings, quality, loadQuality]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    if (mode === 'improve_procedure' && !editTarget && !conversationId) {
      toast.error('Sélectionnez d\'abord la procédure à modifier.');
      return;
    }
    if (mode === 'improve_article' && !editTarget && !conversationId) {
      toast.error('Sélectionnez d\'abord la fiche à modifier.');
      return;
    }
    setInput('');
    setMessages((m) => [...m, { role: 'admin', content }]);
    setLoading(true);
    try {
      const context = {};
      if (mode === 'improve_procedure' && editTarget) context.procedure_slug = editTarget;
      if (mode === 'improve_article' && editTarget) context.article_id = editTarget;
      const r = await api.post('/admin/assistant/ai-chat', { mode, message: content, conversation_id: conversationId, context });
      setConversationId(r.data.conversation_id);
      setMessages((m) => [...m, {
        role: 'assistant', content: r.data.reply,
        proposed_actions: r.data.proposed_actions || [],
        sources: r.data.sources || [], confidence: r.data.confidence,
        uncertain_points: r.data.uncertain_points || [], web_used: r.data.web_used,
      }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur du chat IA admin.');
      setMessages((m) => [...m, { role: 'assistant', content: "Erreur lors de l'analyse." }]);
    } finally {
      setLoading(false);
    }
  };

  const applyAction = async (action) => {
    setApplying(true);
    try {
      const r = await api.post('/admin/assistant/ai-chat/apply', { action });
      toast.success(`Action appliquée : ${r.data.status}`);
      if (action.type === 'update_setting') loadSettings();
      loadAudit();
      setMessages((m) => [...m, { role: 'assistant', content: `✅ Action « ${action.type} » appliquée (${r.data.status}).` }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'appliquer l'action.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div data-testid="admin-ai-chat">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-[#6B6B6E]">Mode :</span>
        {MODES.map((mo) => (
          <button key={mo.id} onClick={() => setMode(mo.id)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${mode === mo.id ? 'border-[#FF6B35] text-[#FF6B35] bg-[#FF6B35]/10' : 'border-[#3A3A3D] text-[#B8B8BA] hover:text-white'}`}
            data-testid={`ai-mode-${mo.id}`}>
            {mo.icon} {mo.label}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowSettings((s) => !s)} className="text-xs px-3 py-1.5 rounded-lg border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid="ai-toggle-settings">
            <Sliders className="h-3.5 w-3.5 inline mr-1" /> Réglages
          </button>
          <button onClick={() => { setShowAudit((s) => !s); loadAudit(); }} className="text-xs px-3 py-1.5 rounded-lg border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid="ai-toggle-audit">
            <ClipboardList className="h-3.5 w-3.5 inline mr-1" /> Audit
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="mb-4 rounded-xl border border-[#3A3A3D] bg-[#0A0A0B] p-4" data-testid="ai-settings-panel">
          <p className="text-sm font-semibold text-white mb-1">Réglages du moteur Nova (effet immédiat)</p>
          <p className="text-[11px] text-[#6B6B6E] mb-3">Chaque modification est validée, auditée et appliquée sans redémarrage.</p>

          {webStatus && (
            <div className="rounded-lg border border-[#3A3A3D] bg-[#111113] p-2.5 mb-3 text-[11px]" data-testid="ai-web-research-status">
              <span className="text-[#6B6B6E]">Recherche Web externe : </span>
              <span className={webStatus.enabled ? 'text-[#00FF88]' : 'text-[#FFB800]'}>{webStatus.label}</span>
              <span className="text-[#6B6B6E]"> · fournisseurs : {(webStatus.supported_providers || []).join(', ')}</span>
            </div>
          )}

          {/* Outil de test du seuil de routage */}
          <div className="rounded-lg border border-[#00FFFF]/30 bg-[#00FFFF]/5 p-3 mb-4" data-testid="ai-routing-test">
            <p className="text-xs font-semibold text-white mb-2">Tester le routage d'un message</p>
            <div className="flex flex-wrap gap-2 items-center">
              <input value={testMsg} onChange={(e) => setTestMsg(e.target.value)}
                placeholder="Ex : ça ne marche pas du tout"
                className="flex-1 min-w-[180px] bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-1.5 text-xs text-white"
                data-testid="ai-test-message" />
              <input value={testThreshold} onChange={(e) => setTestThreshold(e.target.value)}
                placeholder="seuil simulé (ex 0.9)" type="number" step="0.05"
                className="w-36 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-1.5 text-xs text-white"
                data-testid="ai-test-threshold" />
              <button onClick={runTest} disabled={busy || !testMsg.trim()}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#00FFFF]/20 text-[#00FFFF] border border-[#00FFFF]/40 disabled:opacity-40"
                data-testid="ai-test-run">Simuler</button>
            </div>
            {testResult && (
              <div className="mt-2 text-[11px] text-[#B8B8BA] space-y-1" data-testid="ai-test-result">
                <p>Seuil actif : <span className="text-white">{testResult.live.threshold}</span> · Route : <span className="text-[#FF6B35]">{testResult.live.route}</span> · IA sollicitée : <span className="text-white">{testResult.live.would_call_ai ? 'oui' : 'non'}</span> (confiance détectée {testResult.live.deterministic_confidence})</p>
                {testResult.with_threshold && (
                  <p>Seuil simulé {testResult.with_threshold.threshold} : Route <span className="text-[#FF6B35]">{testResult.with_threshold.route}</span> · IA sollicitée {testResult.with_threshold.would_call_ai ? 'oui' : 'non'}</p>
                )}
              </div>
            )}
          </div>

          {/* Simulation prédictive d'impact */}
          <div className="rounded-lg border border-[#FF6B35]/30 bg-[#FF6B35]/5 p-3 mb-4" data-testid="ai-simulation-panel">
            <p className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5 text-[#FF6B35]" /> Simulation d'impact (échantillon historique, sans effet de bord)</p>
            <div className="flex flex-wrap gap-2 items-center text-[11px] text-[#B8B8BA]">
              <span>Échantillon :</span>
              <select value={simSampleCount} onChange={(e) => { setSimSampleCount(e.target.value); setSimSamplePeriod(''); }}
                className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-white" data-testid="sim-sample-count">
                <option value="100">100</option><option value="500">500</option><option value="1000">1000</option>
              </select>
              <span>ou période :</span>
              <select value={simSamplePeriod} onChange={(e) => setSimSamplePeriod(e.target.value)}
                className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-white" data-testid="sim-sample-period">
                <option value="">—</option><option value="7">7 jours</option><option value="30">30 jours</option><option value="90">90 jours</option>
              </select>
              <label className="flex items-center gap-1 ml-2" data-testid="sim-deep-toggle">
                <input type="checkbox" checked={simDeep} onChange={(e) => setSimDeep(e.target.checked)} /> Approfondie
              </label>
              {simDeep && (
                <input type="number" value={simAiLimit} onChange={(e) => setSimAiLimit(e.target.value)} placeholder="limite appels IA"
                  className="w-24 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-white" data-testid="sim-ai-limit" />
              )}
            </div>
            {simResult && (
              <div className="mt-2 text-[11px] text-[#B8B8BA] space-y-1 border-t border-[#3A3A3D] pt-2" data-testid="sim-result">
                <p className="text-white">Diagnostics analysés : {simResult.results.diagnostics_analyzed} · Routage modifié : <span className="text-[#FF6B35]">{simResult.results.routing_changed}</span></p>
                <p>Appels IA : {simResult.results.ai_calls_current} → {simResult.results.ai_calls_proposed} ({simResult.results.ai_calls_pct_change >= 0 ? '+' : ''}{simResult.results.ai_calls_pct_change}%) · Latence moy. : {simResult.results.latency_avg_delta_s >= 0 ? '+' : ''}{simResult.results.latency_avg_delta_s}s</p>
                <p>Cas améliorés : <span className="text-[#00FF88]">{simResult.results.cases_improved}</span> · dégradés : <span className="text-[#FF3B5C]">{simResult.results.cases_degraded}</span> · sans procédure : {simResult.results.cases_without_procedure} · risque de boucle : {simResult.results.loop_risk_cases}</p>
                <p>Étapes moy. : {simResult.results.avg_steps_current} → {simResult.results.avg_steps_proposed}</p>
                {simResult.warning && <p className="text-[#FFB800] font-medium">⚠ {simResult.warning}</p>}
                {simResult.deep && <p className="text-[10px] text-[#6B6B6E]">Approfondie : coût estimé ${simResult.deep.estimated_cost_usd} · temps ~{simResult.deep.estimated_time_s}s (limite {simResult.deep.ai_call_limit})</p>}
                <ul className="list-disc list-inside text-[10px] text-[#6B6B6E]">
                  {(simResult.disclaimers || []).map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            )}

            {/* Simulation approfondie (Claude) — à la demande, limitée */}
            <div className="mt-3 border-t border-[#3A3A3D] pt-2" data-testid="ai-deep-simulation">
              <p className="text-[11px] font-semibold text-white mb-1">Simulation approfondie (Claude) — déterministe vs IA</p>
              <div className="flex flex-wrap gap-2 items-center text-[11px] text-[#B8B8BA]">
                <span>Appels max :</span>
                <select value={deepLimit} onChange={(e) => setDeepLimit(e.target.value)} className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-white" data-testid="deep-limit">
                  <option value="10">10</option><option value="25">25</option><option value="50">50</option>
                </select>
                <button onClick={estimateDeep} disabled={deepBusy} className="text-[11px] px-2.5 py-1 rounded-lg border border-[#00FFFF]/40 text-[#00FFFF] disabled:opacity-40" data-testid="deep-estimate-btn">Estimer</button>
              </div>
              {deepEstimate && (
                <div className="mt-2 text-[11px] text-[#B8B8BA] space-y-1" data-testid="deep-estimate">
                  <p>Cas ambigus : {deepEstimate.ambiguous_cases} · appels max : {deepEstimate.ai_calls_max} · durée ~{deepEstimate.estimated_time_s}s · coût ~${deepEstimate.estimated_cost_usd} · modèle {deepEstimate.model}</p>
                  <p className="text-[10px] text-[#6B6B6E]">Champs transmis (anonymisés) : {(deepEstimate.anonymized_fields || []).join(', ')} · aucun effet sur les sessions réelles</p>
                  <button onClick={runDeep} disabled={deepBusy || deepEstimate.ai_calls_max === 0} className="flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40" data-testid="deep-run-btn">
                    {deepBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3" />} Lancer la simulation
                  </button>
                </div>
              )}
              {deepResult && (
                <div className="mt-2 text-[11px] text-[#B8B8BA] space-y-2" data-testid="deep-result">
                  <p className="text-white">Appels : {deepResult.ai_calls_made} · Accord déterministe/Claude : {Math.round((deepResult.aggregates.agreement_rate||0)*100)}% · Catégories modifiées : {deepResult.aggregates.categories_changed} · Procédures modifiées : {deepResult.aggregates.procedures_changed} · Revue humaine : {deepResult.aggregates.needs_human_review}</p>
                  {(deepResult.comparisons || []).filter((c) => !c.interrupted).map((c, i) => (
                    <div key={i} className="rounded-lg border border-[#3A3A3D] p-2" data-testid="deep-comparison">
                      <p className="text-[#E8E8E8]">{c.message}</p>
                      <p className="text-[10px] text-[#6B6B6E]">Déterministe : {c.deterministic_category}/{c.current_procedure || '—'} · Claude : {c.claude_category}/{c.proposed_procedure || '—'} · conf {c.confidence}{c.degradation_risk ? ' · ⚠ risque' : ''}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {[['current_ok','Actuel correct'],['ai_better','IA meilleur'],['both_ok','Les deux OK'],['none_ok','Aucun'],['create_suggestion','Suggestion']].map(([v, lbl]) => (
                          <button key={v} onClick={() => recordVerdict(c, v)} className="text-[10px] px-2 py-0.5 rounded-full border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid={`deep-verdict-${v}`}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(deepResult.disclaimers || []).length > 0 && <p className="text-[10px] text-[#6B6B6E] italic">{deepResult.disclaimers.join(' ')}</p>}
                </div>
              )}
            </div>

            {/* Score de qualité du routeur */}
            <div className="mt-3 border-t border-[#3A3A3D] pt-2" data-testid="routing-quality-panel">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <p className="text-[11px] font-semibold text-white">Score de qualité du routeur</p>
                <select value={qualityGroup} onChange={(e) => { setQualityGroup(e.target.value); loadQuality(e.target.value); }}
                  className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-[11px] text-white" data-testid="quality-groupby">
                  <option value="">Global</option>
                  <option value="category">Par catégorie</option>
                  <option value="procedure">Par procédure</option>
                  <option value="device">Par appareil</option>
                  <option value="application">Par application</option>
                </select>
                <button onClick={() => loadQuality()} disabled={qualityBusy}
                  className="text-[11px] px-2.5 py-1 rounded-lg border border-[#00FFFF]/40 text-[#00FFFF] disabled:opacity-40" data-testid="quality-refresh">
                  {qualityBusy ? '…' : 'Actualiser'}
                </button>
              </div>
              {quality && (
                <div className="text-[11px] text-[#B8B8BA] space-y-2" data-testid="quality-result">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      ['Cas analysés', quality.cases_analyzed],
                      ['Verdicts renseignés', quality.total_verdicts],
                      ['Sans verdict', quality.cases_without_verdict],
                      ['Batches', quality.batches],
                      ['Accord (verdicts)', `${Math.round((quality.agreement_rate || 0) * 100)}%`],
                      ['Préf. déterministe', `${Math.round((quality.prefer_deterministic_rate || 0) * 100)}%`],
                      ['Préf. Claude', `${Math.round((quality.prefer_ai_rate || 0) * 100)}%`],
                      ['Les deux OK', `${Math.round((quality.both_ok_rate || 0) * 100)}%`],
                      ['Aucun satisfaisant', `${Math.round((quality.none_satisfactory_rate || 0) * 100)}%`],
                      ['Insuffisamment documentés', `${Math.round((quality.insufficient_rate || 0) * 100)}%`],
                      ['Accord moteurs (batch)', `${Math.round((quality.engine_agreement_rate || 0) * 100)}%`],
                      ['Taux de boucle', `${Math.round((quality.loop_rate || 0) * 100)}%`],
                      ['Identifiants hors contexte', `${Math.round((quality.credential_display_rate || 0) * 100)}%`],
                      ['Latence déterministe', `${quality.avg_latency_deterministic_ms} ms`],
                      ['Latence Claude', `${quality.avg_latency_claude_ms} ms`],
                    ].map(([lbl, val], i) => (
                      <div key={i} className="rounded-lg border border-[#3A3A3D] bg-[#0A0A0B] p-2" data-testid="quality-metric">
                        <p className="text-[9px] text-[#6B6B6E] uppercase tracking-wide">{lbl}</p>
                        <p className="text-white text-sm font-semibold">{val}</p>
                      </div>
                    ))}
                  </div>
                  {quality.cases_without_verdict > 0 && (
                    <p className="text-[10px] text-[#FFB800]" data-testid="quality-no-verdict-note">
                      {quality.cases_without_verdict} cas en attente de verdict humain — ni succès, ni échec, exclus des taux d'accord.
                    </p>
                  )}
                  {(quality.breakdown || []).length > 0 && (
                    <div className="rounded-lg border border-[#3A3A3D] p-2" data-testid="quality-breakdown">
                      <p className="text-[10px] text-[#6B6B6E] mb-1">Ventilation ({quality.group_by})</p>
                      {quality.breakdown.map((b, i) => (
                        <p key={i} className="text-[10px]">
                          <span className="text-white">{b.group}</span> — actuel {b.current_ok || 0} · IA {b.ai_better || 0} · deux {b.both_ok || 0} · aucun {b.none_ok || 0} · insuff. {b.insufficient || 0}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sources Web officielles (Tavily) */}
            <div className="mt-3 border-t border-[#3A3A3D] pt-2" data-testid="ai-web-sources">
              <p className="text-[11px] font-semibold text-white mb-1">Rechercher des sources officielles</p>
              <div className="flex flex-wrap gap-2 items-center">
                <input value={srcQuery} onChange={(e) => setSrcQuery(e.target.value)} placeholder="Ex : installer TiviMate Fire TV" className="flex-1 min-w-[200px] bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-1.5 text-xs text-white" data-testid="src-query" />
                <button onClick={searchSources} disabled={srcBusy || !srcQuery.trim()} className="text-xs px-3 py-1.5 rounded-lg bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40 disabled:opacity-40" data-testid="src-search-btn">{srcBusy ? '…' : 'Rechercher'}</button>
              </div>
              {srcResults && (
                <div className="mt-2 space-y-1" data-testid="src-results">
                  {!srcResults.enabled && <p className="text-[11px] text-[#FFB800]">Recherche Web externe non configurée.</p>}
                  {srcResults.query_sent && <p className="text-[10px] text-[#6B6B6E]">Requête envoyée (assainie) : « {srcResults.query_sent} »</p>}
                  {(srcResults.sources || []).map((s, i) => (
                    <div key={i} className="rounded-lg border border-[#3A3A3D] p-2 text-[11px]" data-testid="src-item">
                      <p className="text-white">{s.title} {s.is_official ? <span className="text-[#00FF88]">· officielle</span> : <span className="text-[#FFB800]">· non officielle</span>}</p>
                      <p className="text-[10px] text-[#6B6B6E]">{s.domain} · {s.type} {s.published_date ? `· ${s.published_date}` : ''}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {settings.map((s) => (
              <div key={s.key} className="rounded-lg border border-[#3A3A3D] p-2.5" data-testid={`ai-setting-${s.key}`}>
                <p className="text-xs text-white font-medium">{s.label}</p>
                <p className="text-[11px] text-[#6B6B6E]">{s.key} · type {s.type}{s.min != null ? ` · [${s.min} – ${s.max}]` : ''}</p>
                <p className="text-xs text-[#FF6B35] mt-1">Valeur : {String(s.current_value)} <span className="text-[#6B6B6E]">(défaut {String(s.default)})</span></p>
                <p className="text-[11px] text-[#6B6B6E] italic mt-1">{s.impact}</p>
                {(s.services || []).length > 0 && (
                  <p className="text-[10px] text-[#6B6B6E] mt-1">Services : {s.services.join(', ')}</p>
                )}
                {editing?.key !== s.key ? (
                  <button onClick={() => { setEditing({ key: s.key, value: s.type === 'bool' ? !s.current_value : s.current_value }); setPreview(null); }}
                    className="mt-2 text-[11px] px-2.5 py-1 rounded-lg border border-[#3A3A3D] text-[#B8B8BA] hover:text-white"
                    data-testid={`ai-setting-edit-${s.key}`}>Modifier</button>
                ) : (
                  <div className="mt-2 space-y-2">
                    {s.type === 'bool' ? (
                      <select value={String(editing.value)} onChange={(e) => setEditing({ key: s.key, value: e.target.value === 'true' })}
                        className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-xs text-white w-full"
                        data-testid={`ai-setting-input-${s.key}`}>
                        <option value="true">Activé</option>
                        <option value="false">Désactivé</option>
                      </select>
                    ) : (
                      <input type="number" step={s.type === 'float' ? '0.05' : '1'} value={editing.value}
                        onChange={(e) => setEditing({ key: s.key, value: e.target.value })}
                        className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-2 py-1 text-xs text-white w-full"
                        data-testid={`ai-setting-input-${s.key}`} />
                    )}
                    <div className="flex gap-2">
                      <button onClick={() => requestPreview(s.key, editing.value)} disabled={busy}
                        className="text-[11px] px-2.5 py-1 rounded-lg border border-[#00FFFF]/40 text-[#00FFFF] disabled:opacity-40"
                        data-testid={`ai-setting-preview-${s.key}`}>Aperçu de l'impact</button>
                      <button onClick={() => { setEditing(null); setPreview(null); }}
                        className="text-[11px] px-2.5 py-1 rounded-lg border border-[#3A3A3D] text-[#B8B8BA]">Annuler</button>
                    </div>
                    {preview && preview.key === s.key && (
                      <div className={`rounded-lg border p-2 text-[11px] ${preview.valid ? 'border-[#3A3A3D] bg-[#0A0A0B]' : 'border-[#FF3B5C]/50 bg-[#FF3B5C]/5'}`} data-testid={`ai-setting-preview-box-${s.key}`}>
                        {!preview.valid ? (
                          <p className="text-[#FF3B5C]">{preview.error}</p>
                        ) : (
                          <div className="space-y-1 text-[#B8B8BA]">
                            <p><span className="text-[#6B6B6E]">Différence :</span> <span className="text-white">{preview.difference}</span></p>
                            <p className="italic text-[#6B6B6E]">{preview.impact}</p>
                            {(preview.risks || []).length > 0 && (
                              <ul className="list-disc list-inside text-[#FFB800]">
                                {preview.risks.map((rk, i) => <li key={i}>{rk}</li>)}
                              </ul>
                            )}
                            <p className="text-[10px] text-[#6B6B6E]">Services : {(preview.services || []).join(', ')} · Redémarrage requis : non</p>
                            <div className="flex gap-2 mt-1">
                              <button onClick={() => runSimulation({ type: 'setting', setting: s.key, proposed_value: editing.value })} disabled={simBusy}
                                className="flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-lg border border-[#00FFFF]/40 text-[#00FFFF] disabled:opacity-40"
                                data-testid={`ai-setting-simulate-${s.key}`}>
                                {simBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3" />} Simuler l'impact
                              </button>
                              <button onClick={() => applySetting(s.key, editing.value)} disabled={busy}
                                className="flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-lg bg-[#FF6B35] text-white disabled:opacity-40"
                                data-testid={`ai-setting-apply-${s.key}`}>
                                <Check className="h-3 w-3" /> Appliquer
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showAudit && (
        <div className="mb-4 rounded-xl border border-[#3A3A3D] bg-[#0A0A0B] p-4" data-testid="ai-audit-panel">
          <p className="text-sm font-semibold text-white mb-2">Journal d'audit</p>
          {audit.length === 0 && <p className="text-xs text-[#6B6B6E]">Aucune action enregistrée.</p>}
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {audit.map((a) => (
              <div key={a.id} className="text-[11px] text-[#B8B8BA] border-b border-[#3A3A3D]/40 pb-1">
                <span className="text-white">{a.action_type}</span>
                {a.request?.setting && <span> · {a.request.setting}: {String(a.before)} → {String(a.after)}</span>}
                {a.created_ref && <span> · ref {a.created_ref}</span>}
                <span className="text-[#6B6B6E]"> · {a.created_at ? new Date(a.created_at).toLocaleString('fr-FR') : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-[#3A3A3D] bg-[#111113] h-[420px] overflow-y-auto p-4 space-y-4" data-testid="ai-chat-messages">
        {messages.length === 0 && (
          <p className="text-sm text-[#6B6B6E]">Posez une question technique, demandez une analyse ou une génération de brouillon. Les actions proposées ne sont appliquées qu'après votre confirmation.</p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'admin' ? 'text-right' : ''}>
            <div className={`inline-block text-left rounded-2xl px-4 py-3 text-sm ${m.role === 'admin' ? 'bg-[#00FFFF]/10 text-white' : 'bg-[#1A1A1D] border border-[#3A3A3D] text-[#E8E8E8]'}`}>
              <p className="whitespace-pre-wrap">{m.content}</p>
              {m.role === 'assistant' && (m.confidence || (m.sources && m.sources.length > 0)) && (
                <div className="mt-2 text-[11px] text-[#6B6B6E]">
                  {m.confidence && <span className={CONF[m.confidence] || ''}>Confiance : {m.confidence}</span>}
                  {m.web_used && <span> · sources web</span>}
                  {m.sources && m.sources.length > 0 && <span> · {m.sources.length} source(s)</span>}
                </div>
              )}
            </div>
            {m.role === 'assistant' && m.proposed_actions && m.proposed_actions.map((a, k) => (
              <ActionCard key={k} action={a} onApply={applyAction} applying={applying} />
            ))}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-[#B8B8BA] text-sm">
            <Loader2 className="h-4 w-4 text-[#FF6B35] animate-spin" /> Analyse en cours…
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex flex-col gap-2 mt-3">
        {(mode === 'improve_procedure' || mode === 'improve_article') && (
          <div className="flex items-center gap-2" data-testid="ai-edit-target-row">
            <span className="text-xs text-[#6B6B6E] flex-shrink-0">
              {mode === 'improve_procedure' ? 'Procédure à modifier :' : 'Fiche à modifier :'}
            </span>
            <select
              value={editTarget}
              onChange={(e) => { setEditTarget(e.target.value); setConversationId(null); setMessages([]); }}
              disabled={!!conversationId}
              className="flex-1 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg px-3 py-2 text-xs text-white focus:border-[#FF6B35] focus:outline-none disabled:opacity-50"
              data-testid="ai-edit-target-select">
              <option value="">— Sélectionner —</option>
              {mode === 'improve_procedure' && procedures.map((p) => (
                <option key={p.slug} value={p.slug}>{p.title || p.slug} ({p.status})</option>
              ))}
              {mode === 'improve_article' && articles.map((a) => (
                <option key={a.id} value={a.id}>{a.title}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-end gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder={mode === 'improve_procedure'
            ? 'Ex : modifie l\'étape 3, il faut d\'abord demander à l\'utilisateur d\'essayer de redémarrer l\'application'
            : 'Ex : Pourquoi Nova demande encore les identifiants pour un écran noir avec son ?'}
          className="flex-1 resize-none bg-[#1A1A1D] border border-[#3A3A3D] rounded-xl px-4 py-3 text-sm text-white placeholder-[#6B6B6E] focus:border-[#FF6B35] focus:outline-none"
          data-testid="ai-chat-input"
        />
        <button type="submit" disabled={loading || !input.trim()}
          className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white flex items-center justify-center disabled:opacity-40"
          data-testid="ai-chat-send">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
        </div>
      </form>
    </div>
  );
}
